import { useSyncExternalStore } from 'react'

/** A tiny observable value for long-lived, non-React objects (recorder, uploader, room) that React reads. */
export class Store<T> {
  private listeners = new Set<() => void>()
  constructor(private value: T) {}

  get = (): T => this.value

  set(next: T) {
    if (Object.is(next, this.value)) return
    this.value = next
    this.listeners.forEach((l) => l())
  }

  update(fn: (value: T) => T) {
    this.set(fn(this.value))
  }

  subscribe = (fn: () => void) => {
    this.listeners.add(fn)
    return () => {
      this.listeners.delete(fn)
    }
  }
}

export function useStore<T>(store: Store<T>): T {
  return useSyncExternalStore(store.subscribe, store.get)
}

/** Reads one derived value; the component re-renders only when that value changes. Return primitives or stable references. */
export function useStoreValue<T, V>(store: Store<T>, select: (value: T) => V): V {
  return useSyncExternalStore(store.subscribe, () => select(store.get()))
}
